// @flow
import { all, call, fork, takeEvery, put } from 'redux-saga/effects';

// logo
import { AxiosResponse } from 'axios';
import { api } from 'api';

import {
	CHANGE_LAYOUT,
	CHANGE_LAYOUT_WIDTH,
	CHANGE_SIDEBAR_THEME,
	CHANGE_SIDEBAR_TYPE,
	CHANGE_TOPBAR_THEME,
	TOGGLE_RIGHT_SIDEBAR,
	SHOW_RIGHT_SIDEBAR,
	HIDE_RIGHT_SIDEBAR,
	GET_LOGO,
} from './actionTypes';

import {
	changeSidebarType as changeSidebarTypeAction,
	changeTopbarTheme as changeTopbarThemeAction,
	setLogo,
} from './actions';

/**
 * Changes the body attribute
 */
function changeBodyAttribute(attribute: string, value: string) {
	if (document.body) document.body.setAttribute(attribute, value);
	return true;
}

/**
 * Toggle the class on body
 * @param {*} cssClass
 */
function manageBodyClass(cssClass: string, action = 'toggle') {
	switch (action) {
		case 'add':
			if (document.body) document.body.classList.add(cssClass);
			break;
		case 'remove':
			if (document.body) document.body.classList.remove(cssClass);
			break;
		default:
			if (document.body) document.body.classList.toggle(cssClass);
			break;
	}

	return true;
}

/**
 * Changes the layout type
 * @param {*} param0
 */
function* changeLayout({ payload: layout }: any) {
	try {
		if (layout === 'horizontal') {
			yield put(changeTopbarThemeAction('dark'));
			document.body.removeAttribute('data-sidebar');
			document.body.removeAttribute('data-sidebar-size');
		} else {
			yield put(changeTopbarThemeAction('light'));
		}
		yield call(changeBodyAttribute, 'data-layout', layout);
	} catch (error) {}
}

/**
 * Changes the layout width
 * @param {*} param0
 */
function* changeLayoutWidth({ payload: { width, layoutType } }: any) {
	try {
		if (layoutType === 'vertical') {
			if (width === 'boxed') {
				yield put(changeSidebarTypeAction('icon', false));
			} else {
				yield put(changeSidebarTypeAction('default', false));
			}
		}
		yield call(changeBodyAttribute, 'data-layout-size', width);
	} catch (error) {}
}

/**
 * Changes the left sidebar theme
 * @param {*} param0
 */
function* changeLeftSidebarTheme({ payload: { theme, layoutType } }: any) {
	try {
		yield call(changeBodyAttribute, 'data-sidebar', theme);
		// if(layoutType === "vertical") {
		// 	if(theme === "light")
		// 	//Fire action for changing dark theme of topbar
		// 	yield put(changeTopbarThemeAction('dark'));
		// 	if(theme === "dark")
		// 	//Fire action for changing light theme of topbar
		// 	yield put(changeTopbarThemeAction('light'));
		// }
	} catch (error) {}
}

/**
 * Changes the topbar theme
 * @param {*} param0
 */
function* changeTopbarTheme({ payload: theme }: any) {
	try {
		yield call(changeBodyAttribute, 'data-topbar', theme);
	} catch (error) {}
}

/**
 * Changes the left sidebar type
 * @param {*} param0
 */
function* changeLeftSidebarType({ payload: { sidebarType, isMobile } }: any) {
	try {
		switch (sidebarType) {
			case 'compact':
				yield call(changeBodyAttribute, 'data-sidebar-size', 'small');
				yield call(manageBodyClass, 'sidebar-enable', 'remove');
				yield call(manageBodyClass, 'vertical-collpsed', 'remove');
				break;
			case 'icon':
				yield call(changeBodyAttribute, 'data-keep-enlarged', 'true');
				yield call(manageBodyClass, 'vertical-collpsed', 'add');
				break;
			case 'condensed':
				yield call(manageBodyClass, 'sidebar-enable', 'add');
				if (!isMobile)
					yield call(manageBodyClass, 'vertical-collpsed', 'add');
				break;
			default:
				yield call(changeBodyAttribute, 'data-sidebar-size', '');
				yield call(manageBodyClass, 'sidebar-enable', 'remove');
				if (!isMobile)
					yield call(manageBodyClass, 'vertical-collpsed', 'remove');
				break;
		}
	} catch (error) {}
}

/**
 * Toggles the rightsidebar
 */
function* toggleRightSidebar() {
	try {
		yield call(manageBodyClass, 'right-bar-enabled');
	} catch (error) {}
}

/**
 * Show the rightsidebar
 */
function* showRightSidebar() {
	try {
		yield call(manageBodyClass, 'right-bar-enabled', 'add');
	} catch (error) {}
}

/**
 * Hides the rightsidebar
 */
function* hideRightSidebar() {
	try {
		yield call(manageBodyClass, 'right-bar-enabled', 'remove');
	} catch (error) {}
}

/**
 * Sets topbar logo to user's logo
 */
function* fetchLogo() {
	try {
		const response: AxiosResponse<string> =
			yield api.logo.apiLogoGetLogoLinkGet();
		yield put(setLogo(response.data));
		// if (response.status === 200) {
		//     yield put(setLogo(response.data));
		// }
	} catch (error: any) {
		console.log('LOGO GET ERROR', error);
	}
}

/**
 * Watchers
 */
export function* watchChangeLayoutType() {
	yield takeEvery(CHANGE_LAYOUT, changeLayout);
}

export function* watchChangeLayoutWidth() {
	yield takeEvery(CHANGE_LAYOUT_WIDTH, changeLayoutWidth);
}

export function* watchChangeLeftSidebarTheme() {
	yield takeEvery(CHANGE_SIDEBAR_THEME, changeLeftSidebarTheme);
}

export function* watchChangeLeftSidebarType() {
	yield takeEvery(CHANGE_SIDEBAR_TYPE, changeLeftSidebarType);
}

export function* watchChangeTopbarTheme() {
	yield takeEvery(CHANGE_TOPBAR_THEME, changeTopbarTheme);
}

export function* watchToggleRightSidebar() {
	yield takeEvery(TOGGLE_RIGHT_SIDEBAR, toggleRightSidebar);
}

export function* watchShowRightSidebar() {
	yield takeEvery(SHOW_RIGHT_SIDEBAR, showRightSidebar);
}

export function* watchHideRightSidebar() {
	yield takeEvery(HIDE_RIGHT_SIDEBAR, hideRightSidebar);
}

export function* watchFetchLogo() {
	yield takeEvery(GET_LOGO, fetchLogo);
}

function* LayoutSaga() {
	yield all([
		fork(watchChangeLayoutType),
		fork(watchChangeLayoutWidth),
		fork(watchChangeLeftSidebarTheme),
		fork(watchChangeLeftSidebarType),
		fork(watchToggleRightSidebar),
		fork(watchShowRightSidebar),
		fork(watchHideRightSidebar),
		fork(watchChangeTopbarTheme),
		fork(watchFetchLogo),
	]);
}

export default LayoutSaga;
